import { useState, useEffect, useRef } from "react";
import type { Theme, FontPairing } from "@structurify/shared-types";

interface Props {
  theme: Theme;
  fonts: FontPairing;
  onCapture: (file: File) => void;
  onCancel: () => void;
}

type CameraPhase = "aim" | "captured" | "confirming";
type CameraError = "unavailable" | "denied" | null;

export function StageCamera({ theme, fonts, onCapture, onCancel }: Props) {
  const [phase, setPhase] = useState<CameraPhase>("aim");
  const [edgeProgress, setEdgeProgress] = useState(0);
  const [error, setError] = useState<CameraError>(null);
  const [capturedUrl, setCapturedUrl] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("unavailable");
      return;
    }
    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } } })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play();
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const name = err instanceof Error ? err.name : "";
        setError(name === "NotAllowedError" ? "denied" : "unavailable");
      });
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  useEffect(() => {
    if (phase !== "aim") return;
    let p = 0;
    const id = setInterval(() => {
      p = Math.min(1, p + 0.04 + Math.random() * 0.04);
      setEdgeProgress(p);
    }, 80);
    return () => clearInterval(id);
  }, [phase]);

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    setCapturedUrl(canvas.toDataURL("image/jpeg", 0.95));
    setPhase("captured");
    setTimeout(() => setPhase("confirming"), 380);
  };

  const usePhoto = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `scan_${Date.now()}.jpg`, { type: "image/jpeg" });
      streamRef.current?.getTracks().forEach((t) => t.stop());
      onCapture(file);
    }, "image/jpeg", 0.95);
  };

  if (error) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#0a0a09", color: "#fff", gap: 16 }}>
        <div style={{ fontFamily: fonts.mono, fontSize: 11, color: "rgba(255,255,255,0.5)", letterSpacing: "0.1em" }}>
          {error === "denied" ? "CAMERA ACCESS DENIED" : "CAMERA UNAVAILABLE"}
        </div>
        <div style={{ fontFamily: fonts.sans, fontSize: 14, color: "rgba(255,255,255,0.7)", textAlign: "center", maxWidth: 280, lineHeight: 1.5 }}>
          {error === "denied"
            ? "Allow camera access in your browser settings, then try again."
            : "No camera found on this device. Use file upload instead."}
        </div>
        <button onClick={onCancel} style={{ marginTop: 8, background: "transparent", border: "1px solid rgba(255,255,255,0.2)", color: "#fff", fontFamily: fonts.sans, fontSize: 13, padding: "10px 20px", cursor: "pointer" }}>
          Use file upload
        </button>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#0a0a09", color: "#fff", overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", padding: "14px 18px", borderBottom: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 }}>
        <button onClick={onCancel} style={{ background: "transparent", border: "none", color: "#fff", fontFamily: fonts.sans, fontSize: 14, cursor: "pointer", padding: 6 }}>← Cancel</button>
        <div style={{ flex: 1 }} />
        <span style={{ fontFamily: fonts.mono, fontSize: 10, color: "rgba(255,255,255,0.6)", letterSpacing: "0.08em" }}>SCAN · DOCUMENT</span>
      </div>

      <div style={{ flex: 1, position: "relative", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {phase === "aim" ? (
          <video
            ref={videoRef}
            playsInline
            muted
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          capturedUrl && (
            <img
              src={capturedUrl}
              alt="Captured document"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", transform: phase === "captured" ? "scale(0.92)" : "none", transition: "transform 320ms cubic-bezier(.2,.7,.2,1)" }}
            />
          )
        )}

        {phase === "aim" && (
          <svg style={{ position: "absolute", inset: "10%", pointerEvents: "none" }} viewBox="0 0 100 100" preserveAspectRatio="none">
            <rect x="0" y="0" width="100" height="100" fill="none" stroke={theme.accent} strokeWidth="0.4" strokeDasharray={`${edgeProgress * 400} 400`} style={{ transition: "stroke-dasharray 80ms linear" }} />
            {([[0, 0], [100, 0], [0, 100], [100, 100]] as Array<[number, number]>).map(([x, y], i) => (
              <g key={i} stroke={theme.accent} strokeWidth="1.5" fill="none">
                <line x1={x} y1={y} x2={x + (x === 0 ? 10 : -10)} y2={y} />
                <line x1={x} y1={y} x2={x} y2={y + (y === 0 ? 10 : -10)} />
              </g>
            ))}
          </svg>
        )}

        <div style={{ position: "absolute", bottom: 110, left: "50%", transform: "translateX(-50%)", fontFamily: fonts.mono, fontSize: 10, letterSpacing: "0.1em", color: "rgba(255,255,255,0.85)", padding: "6px 12px", background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.16)", whiteSpace: "nowrap" }}>
          {phase === "aim" && (edgeProgress > 0.85 ? "EDGES LOCKED · TAP TO CAPTURE" : "HOLD STEADY · DETECTING EDGES")}
          {phase === "captured" && "CAPTURED"}
          {phase === "confirming" && "TAP USE PHOTO TO CONTINUE"}
        </div>
      </div>

      <canvas ref={canvasRef} style={{ display: "none" }} />

      <div style={{ flexShrink: 0, padding: "20px 18px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 18 }}>
        <button onClick={onCancel} style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.7)", fontFamily: fonts.sans, fontSize: 13, cursor: "pointer", padding: "10px 14px" }}>Retake</button>

        {phase === "aim" && (
          <button
            onClick={capture}
            disabled={edgeProgress < 0.85}
            style={{ width: 64, height: 64, borderRadius: 64, background: edgeProgress > 0.85 ? "#fff" : "rgba(255,255,255,0.3)", border: "4px solid rgba(255,255,255,0.4)", cursor: edgeProgress > 0.85 ? "pointer" : "default", boxShadow: "0 0 0 2px #fff", transition: "background 200ms" }}
          />
        )}

        {(phase === "captured" || phase === "confirming") && (
          <button onClick={usePhoto} style={{ background: "#fff", color: "#0a0a09", fontFamily: fonts.sans, fontSize: 14, fontWeight: 500, padding: "12px 20px", border: "none", cursor: "pointer" }}>
            Use photo →
          </button>
        )}

        <button onClick={onCancel} style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.7)", fontFamily: fonts.sans, fontSize: 13, cursor: "pointer", padding: "10px 14px" }}>Files</button>
      </div>
    </div>
  );
}
